import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import gsap from "gsap";
import { Flip } from "gsap/Flip";
import "./landing-page.css";
import { DimensionalGoldButton, GhostButton } from "./components/v2/ActionRow";
import { resolvePastedGiftLink } from "./gift-routing";
import useModalDialog from "./hooks/useModalDialog";

gsap.registerPlugin(Flip);

const GLYPH_ROOT = "/glyphs/tgc-124";
const VOICE_MIC_POSTER = `${GLYPH_ROOT}/microphone-v1.png`;
const THRESHOLD_DESKTOP = "/backgrounds/tgc-124/threshold-desktop-v1.png";
const THRESHOLD_MOBILE = "/backgrounds/tgc-124/threshold-mobile-v1.png";
const THRESHOLD_CONTINUATION = "/backgrounds/tgc-124/threshold-continuation-v1.png";

const LANDING_PLATES = {
  pitch: "/backgrounds/tgc-95-full-repaint-s1-raw-v2.png",
  mechanics: "/backgrounds/tgc-95-full-repaint-s2-raw-v1.png",
  prompt: "/backgrounds/tgc-95-full-repaint-s3-raw-v1.png",
  reassuranceA: "/backgrounds/tgc-95-full-repaint-s4a-raw-v1.png",
  reassuranceB: "/backgrounds/tgc-95-full-repaint-s4b-raw-v1.png",
  reassuranceC: "/backgrounds/tgc-95-full-repaint-s4c-raw-v1.png",
};

const plateStyle = (plate) => ({
  "--tgc-v2-bg-image": `url(${plate})`,
});

const getLandingClassName = () => {
  if (typeof window === "undefined") {
    return "landing-v2";
  }

  const params = new URLSearchParams(window.location.search);
  const classes = ["landing-v2"];

  if (params.get("closingHeadline") === "gradient") {
    classes.push("landing-v2--closing-headline-gradient");
  }

  if (params.get("plateMeasure") === "1" || params.get("s4Measure") === "1") {
    classes.push("landing-v2--plate-measure");
  }

  return classes.join(" ");
};

const isPlateMeasureEnabled = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get("plateMeasure") === "1" || params.get("s4Measure") === "1";
};

const STEPS = [
  {
    number: "1",
    title: "You set the stage",
    body: "Share a few things about someone you care about — what lights them up, what’s on their mind, how they like to be talked to. Takes about five minutes.",
  },
  {
    number: "2",
    title: "They have the conversation",
    body: "They open a link and talk — by text or voice — with an AI that already knows a few things about them. No script, no agenda. Just a real conversation.",
  },
  {
    number: "3",
    title: "The conversation becomes a keepsake",
    body: "When the conversation ends, they receive a written guide built from everything they shared — so any AI can actually be useful to them from the first message.",
  },
];

const GLYPHS = {
  book: { src: `${GLYPH_ROOT}/book-v1.png` },
  envelope: { src: `${GLYPH_ROOT}/envelope-v1.png` },
  giftBox: { src: `${GLYPH_ROOT}/gift-box-v1.png` },
  infinity: { src: `${GLYPH_ROOT}/continuity-v1.png` },
  lantern: { src: `${GLYPH_ROOT}/lantern-v1.png` },
  locket: { src: `${GLYPH_ROOT}/locket-v1.png` },
};

function ReasonGlyph({ glyph }) {
  return <img src={glyph.src} alt="" />;
}

const REASONS = [
  {
    icon: GLYPHS.locket,
    title: "They feel truly seen",
    body: "A conversation that reflects who they actually are, not who they perform as.",
    expand:
      "The introducer answers twelve questions about them — their work, what they care about, how they talk, what's happening in their life right now. The AI uses all of that to have a conversation shaped by someone who knows them, not a chatbot asking generic questions.",
  },
  {
    icon: GLYPHS.giftBox,
    title: "A gift they keep",
    body: "Most gifts get used once. This one gets used every time they open an AI.",
    expand:
      "The keepsake is a custom prompt they can paste into ChatGPT, Claude, or any AI — so they don't have to start from scratch. They can use it for homework, work projects, or just talking through things. Wherever they use it, they can start as themselves instead of explaining themselves first.",
  },
  {
    icon: GLYPHS.book,
    title: "It doesn’t start from zero",
    body: "Instead of starting from scratch, AI starts with context that matters.",
    expand:
      "Most AI conversations start with a person explaining themselves. This one doesn’t. Someone who knows them has already shared what matters — their work, their personality, what’s happening in their life right now. The conversation gets to begin somewhere meaningful instead of starting from zero.",
  },
  {
    icon: GLYPHS.lantern,
    title: "A gentle start to AI",
    body: "For people who haven’t figured out where AI fits — or weren’t sure they wanted it to.",
    expand:
      "AI is already changing how people think, work, and get things done — and the people who'd benefit most are often the ones who assume it isn't for them. This is a gentle way in: not a pitch, not a tutorial, but a conversation that starts with what matters to them, created by someone who knows them. No right answers, no pressure to be impressed — just a real exchange that opens the door to what's possible, in a way that still feels human.",
  },
  {
    icon: GLYPHS.infinity,
    title: "Personal and lasting",
    body: "Built from a real conversation, not a template. Made for one person only.",
    expand:
      "Every gift is unique because every person is. The conversation isn’t a script — no two people get the same one. The keepsake isn’t generic advice — it’s a portrait of them in words, built from a conversation that was actually theirs.",
  },
  {
    icon: GLYPHS.envelope,
    title: "Private by design",
    body: "Their context shapes the gift and is removed on a short schedule.",
    expand:
      "The introducer's answers shape the recipient's conversation; they are not presented as a questionnaire or answer sheet. TGC clears personal gift content on its published retention schedule. The recipient can save their keepsake to keep it, while TGC removes the hosted copy after seven days.",
  },
];

const VOICE_MODAL = {
  title: "Voice mode",
  intro:
    "If they choose voice, the conversation happens as a real phone-style call — they talk, the AI talks back.",
  closer:
    "Same conversation, same keepsake at the end — just spoken instead of typed.",
};

function LandingWordmark({ className = "" }) {
  return (
    <div
      className={`landing-wordmark${className ? ` ${className}` : ""}`}
      aria-label="The Gift Conversation"
    >
      <span>The Gift</span>
      <span>Conversation</span>
    </div>
  );
}

function LandingHeader() {
  return (
    <header className="landing-header">
      <div data-measure="S1 header wordmark">
        <LandingWordmark />
      </div>
      <span className="landing-ornament-anchor" aria-hidden="true" />
    </header>
  );
}

function PlateMeasurementPanel({ measurements }) {
  if (!measurements.length) {
    return null;
  }

  const rows = measurements.flatMap((section) =>
    [
      {
        plate: section.plate,
        item: "section height / scroll height",
        viewport: `${section.sectionHeight} / ${section.scrollHeight}`,
        canvas: "1008x1792",
      },
      ...section.items.map((item) => ({
        plate: section.plate,
        item: item.label,
        viewport: `${item.viewport.x},${item.viewport.y} ${item.viewport.width}x${item.viewport.height}`,
        canvas: `${item.plate.x},${item.plate.y} ${item.plate.width}x${item.plate.height}`,
      })),
    ]
  );

  const table = [
    "| Plate | Item | Viewport px | Plate coords (1008x1792) |",
    "| --- | --- | --- | --- |",
    ...rows.map(
      (row) =>
        `| ${row.plate} | ${row.item} | ${row.viewport} | ${row.canvas} |`
    ),
  ].join("\n");

  return (
    <aside className="landing-plate-measurement" aria-live="polite">
      <strong>Plate measurement</strong>
      <span>Canvas: 1008x1792</span>
      <textarea readOnly value={table} aria-label="Plate measurement table" />
    </aside>
  );
}

function LandingExpandOverlay({ item, onClose, panelRef }) {
  if (!item) {
    return null;
  }

  return (
    <div
      className="landing-expand-layer"
      data-modal-layer
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        ref={panelRef}
        className="landing-expand-panel landing-ghost-frost"
        role="dialog"
        aria-modal="true"
        aria-labelledby="landing-expand-title"
        tabIndex={-1}
        data-flip-id={item.flipId}
      >
        <button
          className="landing-expand-close"
          type="button"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        {item.kind === "voice" ? (
          <div className="landing-expand-content landing-expand-content--voice">
            <span className="landing-voice-kicker">VOICE MODE</span>
            <h2 id="landing-expand-title">{VOICE_MODAL.title}</h2>
            <div className="landing-voice-mic landing-voice-mic--modal" aria-hidden="true">
              <img className="landing-voice-mic-poster" src={VOICE_MIC_POSTER} alt="" />
            </div>
            <p>{VOICE_MODAL.intro}</p>
            <p>{VOICE_MODAL.closer}</p>
          </div>
        ) : (
          <div className="landing-expand-content">
            <div className="landing-reason-icon" aria-hidden="true">
              <ReasonGlyph glyph={item.icon} />
            </div>
            <h2 id="landing-expand-title">{item.title}</h2>
            <p>{item.expand}</p>
          </div>
        )}
      </section>
    </div>
  );
}

export default function LandingPage({
  onCreateGift,
  onRecoverPurchase,
  hasSavedPurchase = false,
  isLoading,
}) {
  const [plateMeasurements, setPlateMeasurements] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showReceiverHelp, setShowReceiverHelp] = useState(false);
  const [receiverLink, setReceiverLink] = useState("");
  const [receiverLinkError, setReceiverLinkError] = useState("");
  const showPlateMeasurement = isPlateMeasureEnabled();
  const prefersReducedMotion = useRef(false);
  const activeTriggerRef = useRef(null);
  const panelRef = useRef(null);
  const receiverTriggerRef = useRef(null);
  const receiverPanelRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => {
      prefersReducedMotion.current = media.matches;
    };

    updatePreference();
    media.addEventListener?.("change", updatePreference);

    return () => {
      media.removeEventListener?.("change", updatePreference);
    };
  }, []);

  useEffect(() => {
    if (!showPlateMeasurement || typeof window === "undefined") {
      return undefined;
    }

    const measure = () => {
      const sections = Array.from(document.querySelectorAll("[data-plate]"));

      setPlateMeasurements(
        sections.map((section) => {
          const sectionRect = section.getBoundingClientRect();
          const scaleX = 1008 / sectionRect.width;
          const scaleY = 1792 / sectionRect.height;
          const items = Array.from(
            section.querySelectorAll("[data-measure]")
          ).map((item) => {
            const rect = item.getBoundingClientRect();
            const viewport = {
              x: Math.round(rect.left - sectionRect.left),
              y: Math.round(rect.top - sectionRect.top),
              width: Math.round(rect.width),
              height: Math.round(rect.height),
            };

            return {
              label: item.getAttribute("data-measure"),
              viewport,
              plate: {
                x: Math.round(viewport.x * scaleX),
                y: Math.round(viewport.y * scaleY),
                width: Math.round(viewport.width * scaleX),
                height: Math.round(viewport.height * scaleY),
              },
            };
          });

          return {
            plate: section.getAttribute("data-plate"),
            viewportWidth: Math.round(window.innerWidth),
            sectionHeight: Math.round(sectionRect.height),
            scrollHeight: Math.round(section.scrollHeight),
            items,
          };
        })
      );
    };

    const frame = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
    };
  }, [showPlateMeasurement]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const animateFlip = (state, target, onComplete) => {
    if (!state || !target || prefersReducedMotion.current) {
      onComplete?.();
      return;
    }

    Flip.from(state, {
      targets: target,
      duration: 0.46,
      ease: "power3.out",
      absolute: true,
      scale: true,
      fade: true,
      prune: true,
      onComplete,
    });
  };

  const openExpandedItem = (item, event) => {
    const trigger = event.currentTarget;
    const state = Flip.getState(trigger);
    activeTriggerRef.current = trigger;
    flushSync(() => setExpandedItem(item));
    if (panelRef.current) {
      trigger.style.visibility = "hidden";
    }
    animateFlip(state, panelRef.current);
  };

  const closeExpandedItem = () => {
    const panel = panelRef.current;
    const trigger = activeTriggerRef.current;
    const state = panel ? Flip.getState(panel) : null;
    flushSync(() => setExpandedItem(null));
    if (trigger) {
      trigger.style.visibility = "";
    }
    animateFlip(state, trigger, () => {
      if (trigger) {
        trigger.style.visibility = "";
      }
      activeTriggerRef.current = null;
    });
  };

  const closeReceiverHelp = () => {
    setShowReceiverHelp(false);
    setReceiverLinkError("");
  };

  const openPastedGiftLink = (event) => {
    event.preventDefault();
    const result = resolvePastedGiftLink(receiverLink, window.location.origin);

    if (result.error) {
      setReceiverLinkError(result.error);
      return;
    }

    window.location.assign(result.destination);
  };

  useModalDialog({
    isOpen: Boolean(expandedItem),
    dialogRef: panelRef,
    returnFocusRef: activeTriggerRef,
    onClose: closeExpandedItem,
  });

  useModalDialog({
    isOpen: showReceiverHelp,
    dialogRef: receiverPanelRef,
    returnFocusRef: receiverTriggerRef,
    onClose: closeReceiverHelp,
  });

  const restingFlipId = (flipId) =>
    expandedItem?.flipId === flipId ? undefined : flipId;

  return (
    <div className={getLandingClassName()}>
      <main className="landing-main" aria-label="The Gift Conversation landing page">
      <section
        className="landing-section landing-section-pitch"
        style={{
          ...plateStyle(LANDING_PLATES.pitch),
          "--tgc-threshold-desktop": `url(${THRESHOLD_DESKTOP})`,
          "--tgc-threshold-mobile": `url(${THRESHOLD_MOBILE})`,
        }}
        data-plate="S1"
      >
        <LandingHeader />

        <div className="landing-pitch-copy" data-measure="S1 copy block">
          <h1>
            <span>
              <span className="landing-headline-initial">A</span> conversation
              becomes a gift
            </span>{" "}
            <strong>they keep.</strong>
          </h1>
          <p>
            You introduce someone you care about. They have a personal AI
            conversation, and leave with a written keepsake — a guide any AI can
            use to understand them and actually be useful{" "}
            <span className="landing-nowrap">to them.</span>
          </p>
          <div className="landing-pitch-actions">
            <GhostButton
              className="tgc-v2-button tgc-v2-button--ghost landing-pill-primary landing-hero-primary"
              onClick={() => scrollToSection("landing-how-it-works")}
              data-measure="S1 How it works CTA"
            >
              How it works <span aria-hidden="true">→</span>
            </GhostButton>
            <button
              ref={receiverTriggerRef}
              className="landing-receiver-link"
              type="button"
              onClick={() => setShowReceiverHelp(true)}
              aria-haspopup="dialog"
            >
              I received an invitation
            </button>
            <button
              className="landing-receiver-link"
              type="button"
              onClick={onRecoverPurchase}
            >
              {hasSavedPurchase ? "Continue my gift" : "I already paid"}
            </button>
          </div>
          <p className="landing-ai-disclosure">
            This experience uses AI, and always tells you when.
          </p>
        </div>
      </section>

      <section
        id="landing-how-it-works"
        className="landing-section landing-section-mechanics"
        style={{
          ...plateStyle(LANDING_PLATES.mechanics),
          "--tgc-threshold-continuation": `url(${THRESHOLD_CONTINUATION})`,
        }}
        data-plate="S2"
      >
        <div className="landing-mechanics-copy">
          <div className="landing-threshold-intro">
            <h2>Introduced with care.<br />Made useful through conversation.</h2>
            <p>
              The Gift Conversation turns an introduction into something lasting:
              a personal AI conversation and a written guide they can use anywhere.
            </p>
          </div>
          <h2 data-measure="S2 title">How It Works</h2>
          <span className="landing-ornament-anchor landing-ornament-anchor-section" aria-hidden="true" />
          <div className="landing-steps" aria-label="How it works">
            {STEPS.map((step) => (
              <article
                className="landing-step"
                key={step.number}
                data-measure={`S2 card ${step.number}: ${step.title}`}
              >
                <div className="landing-step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
          <GhostButton
            className="tgc-v2-button tgc-v2-button--ghost landing-pill-primary landing-read-on landing-ghost-frost"
            onClick={() => scrollToSection("landing-prompt")}
            data-measure="S2 Read on CTA"
          >
            Read on
          </GhostButton>
        </div>
      </section>

      <section
        id="landing-prompt"
        className="landing-section landing-section-prompt"
        style={plateStyle(LANDING_PLATES.prompt)}
        data-plate="S3"
      >
        <div className="landing-prompt-copy">
          <h2 data-measure="S3 title">What’s a ‘prompt’?</h2>
          <span className="landing-ornament-anchor landing-ornament-anchor-section" aria-hidden="true" />
          <p data-measure="S3 body">
            A prompt is a set of instructions you give an AI so it knows how to
            help you. Most are short and forgettable. The keepsake from this
            conversation isn’t — it’s a personal one, built from their
            conversation, that they can paste into any AI so they don’t have to
            explain who they are every time.
          </p>
          <GhostButton
            className="tgc-v2-button tgc-v2-button--ghost landing-pill-primary landing-read-on landing-ghost-frost"
            onClick={() => scrollToSection("landing-reassurance-a")}
            data-measure="S3 Why it matters CTA"
          >
            Why it matters
          </GhostButton>
        </div>
      </section>

      <section
        id="landing-reassurance-a"
        className="landing-section landing-section-reassurance landing-section-reassurance-a"
        style={plateStyle(LANDING_PLATES.reassuranceA)}
        data-plate="S4a"
      >
        <div className="landing-reassurance-copy landing-reassurance-copy-a">
          <h2 data-measure="S4a title">Why It Matters</h2>
          <span className="landing-ornament-anchor landing-ornament-anchor-section" aria-hidden="true" />
          <div className="landing-reasons" aria-label="Why it matters">
            {REASONS.slice(0, 3).map((reason, index) => {
              const flipId = `landing-card-${index + 1}`;
              return (
                <article
                  className="landing-reason-shell"
                  key={reason.title}
                  data-measure={`S4a card ${index + 1}: ${reason.title}`}
                >
                  <button
                    className="landing-reason landing-expand-trigger"
                    type="button"
                    data-flip-id={restingFlipId(flipId)}
                    onClick={(event) =>
                      openExpandedItem(
                        { ...reason, kind: "card", flipId },
                        event
                      )
                    }
                    aria-label={`Open ${reason.title}`}
                    aria-expanded={expandedItem?.flipId === flipId}
                  >
                    <div className="landing-reason-icon" aria-hidden="true">
                      <ReasonGlyph glyph={reason.icon} />
                    </div>
                    <h3>{reason.title}</h3>
                    <p>{reason.body}</p>
                    <span className="landing-expand-indicator" aria-hidden="true">
                      more <span>⌄</span>
                    </span>
                  </button>
                </article>
              );
            })}
          </div>
          <GhostButton
            className="tgc-v2-button tgc-v2-button--ghost landing-pill-primary landing-read-on landing-s4-ghost-pill landing-ghost-frost"
            onClick={() => scrollToSection("landing-reassurance-b")}
            data-measure="S4a Read on CTA"
          >
            Read on
          </GhostButton>
        </div>
      </section>

      <section
        id="landing-reassurance-b"
        className="landing-section landing-section-reassurance landing-section-reassurance-b"
        style={plateStyle(LANDING_PLATES.reassuranceB)}
        data-plate="S4b"
      >
        <div className="landing-reassurance-copy landing-reassurance-copy-b">
          <div className="landing-reasons" aria-label="Why it matters continued">
            {REASONS.slice(3).map((reason, index) => {
              const flipId = `landing-card-${index + 4}`;
              return (
                <article
                  className="landing-reason-shell"
                  key={reason.title}
                  data-measure={`S4b card ${index + 4}: ${reason.title}`}
                >
                  <button
                    className="landing-reason landing-expand-trigger"
                    type="button"
                    data-flip-id={restingFlipId(flipId)}
                    onClick={(event) =>
                      openExpandedItem(
                        { ...reason, kind: "card", flipId },
                        event
                      )
                    }
                    aria-label={`Open ${reason.title}`}
                    aria-expanded={expandedItem?.flipId === flipId}
                  >
                    <div className="landing-reason-icon" aria-hidden="true">
                      <ReasonGlyph glyph={reason.icon} />
                    </div>
                    <h3>{reason.title}</h3>
                    <p>{reason.body}</p>
                    <span className="landing-expand-indicator" aria-hidden="true">
                      more <span>⌄</span>
                    </span>
                  </button>
                </article>
              );
            })}
          </div>
          <button
            className="landing-voice-note landing-expand-trigger landing-ghost-frost"
            type="button"
            data-flip-id={restingFlipId("landing-voice-mode")}
            data-measure="S4b Voice strip"
            onClick={(event) =>
              openExpandedItem(
                { kind: "voice", title: VOICE_MODAL.title, flipId: "landing-voice-mode" },
                event
              )
            }
            aria-label="Open Voice mode"
            aria-expanded={expandedItem?.flipId === "landing-voice-mode"}
          >
            <div className="landing-voice-mic landing-voice-mic--card" aria-hidden="true">
              <img className="landing-voice-mic-poster" src={VOICE_MIC_POSTER} alt="" />
            </div>
            <span className="landing-voice-kicker">VOICE MODE</span>
            <span className="landing-voice-title">Talk it out.</span>
            <p>
              Some conversations want to be spoken, not typed. Voice mode is the
              same experience — just out loud.
            </p>
            <span className="landing-expand-indicator" aria-hidden="true">
              more <span>⌄</span>
            </span>
          </button>
          <GhostButton
            className="tgc-v2-button tgc-v2-button--ghost landing-pill-primary landing-read-on landing-s4-ghost-pill landing-ghost-frost"
            onClick={() => scrollToSection("landing-reassurance-c")}
            data-measure="S4b Give the gift CTA"
          >
            Give the gift
          </GhostButton>
        </div>
      </section>

      <section
        id="landing-reassurance-c"
        className="landing-section landing-section-reassurance landing-section-reassurance-c"
        style={plateStyle(LANDING_PLATES.reassuranceC)}
        data-plate="S4c"
      >
        <div className="landing-reassurance-copy landing-reassurance-copy-c">
          <div className="landing-closing" data-measure="S4c closing block">
            <h2>Give the gift of being understood.</h2>
            <p>One conversation. One keepsake. Made for someone you care about.</p>
            <DimensionalGoldButton
              className="tgc-v2-button tgc-v2-button--gold landing-pill-primary landing-closing-cta"
              onClick={onCreateGift}
              disabled={isLoading}
              data-disabled={isLoading ? "true" : "false"}
              data-measure="S4c Create a Gift CTA"
            >
              {isLoading ? "Loading..." : "Create a Gift"}
            </DimensionalGoldButton>
            <button
              className="landing-purchase-recovery-link"
              type="button"
              onClick={onRecoverPurchase}
            >
              {hasSavedPurchase ? "Continue your gift" : "Already paid? Continue your gift"}
            </button>
          </div>
        </div>
      </section>
      </main>
      <footer className="landing-site-footer" aria-label="Site footer">
        <LandingWordmark className="landing-wordmark-signoff" />
        <nav className="landing-site-footer-nav" aria-label="Supporting information">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/faq">FAQ</a>
          <a href="mailto:support@thegiftconversation.com">Contact</a>
        </nav>
        <small>© 2026 The Gift Conversation, LLC.</small>
      </footer>
      {showPlateMeasurement && (
        <PlateMeasurementPanel measurements={plateMeasurements} />
      )}
      <LandingExpandOverlay
        item={expandedItem}
        onClose={closeExpandedItem}
        panelRef={panelRef}
      />
      {showReceiverHelp && (
        <div
          className="landing-receiver-layer"
          data-modal-layer
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeReceiverHelp();
            }
          }}
        >
          <section
            ref={receiverPanelRef}
            className="landing-receiver-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="landing-receiver-title"
            tabIndex={-1}
          >
            <button
              className="landing-receiver-close"
              type="button"
              onClick={closeReceiverHelp}
              aria-label="Close"
            >
              ×
            </button>
            <span className="landing-receiver-kicker">YOUR ENTRANCE</span>
            <h2 id="landing-receiver-title">Open the personal link you received.</h2>
            <p>
              Your invitation opens directly into the conversation prepared for
              you. Use that link wherever you received it. If you copied it here
              instead, you can open it below.
            </p>
            <form className="landing-receiver-form" onSubmit={openPastedGiftLink} noValidate>
              <label htmlFor="landing-gift-link">Paste your gift link</label>
              <div className="landing-receiver-form-row">
                <input
                  id="landing-gift-link"
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  value={receiverLink}
                  onChange={(event) => {
                    setReceiverLink(event.target.value);
                    if (receiverLinkError) setReceiverLinkError("");
                  }}
                  aria-describedby={receiverLinkError ? "landing-gift-link-error" : undefined}
                  aria-invalid={receiverLinkError ? "true" : "false"}
                  placeholder="https://thegiftconversation.com/gift?id=…"
                />
                <button type="submit">Open gift</button>
              </div>
              {receiverLinkError && (
                <p className="landing-receiver-error" id="landing-gift-link-error" role="alert">
                  {receiverLinkError}
                </p>
              )}
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
